import React, {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {defaultErrorHandler} from '../../context/context';
import customFormsV2Client from '../../infrastructure/acerill/custom-forms-v2-client';
import timezoneService from '../../services/timezone-service';
import Section from '../../libs/react-components-admin/section/section';
import CenteredSpinner from '../../libs/react-components-admin/spinners/centered-spinner';

const tableModelColumns = (timezone, submissions) => {
  const result = [{label: 'Submitted at [' + timezone+ ']'}];
  let lastIndex = 0;
  (submissions || []).forEach(submission => {
    (submission.values || []).forEach(value => {
      const index = result.findIndex(column => column.fieldId === value.fieldId);
      if (index >= 0) {
        lastIndex = index;
      } else {
        result.splice(
          lastIndex + 1, 0,
          {fieldId: value.fieldId, label: value.label}
        );
        lastIndex++;
      }
    });
  });
  return result;
};

const escapeHtml = unsafe => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const submissionsToTableModel = (timezone, submissions) => {
  const columns = tableModelColumns(timezone, submissions);
  return {
    header: columns.map(c => c.label),
    body: submissions.map(submission => columns.map((col, i) => {
      if (i === 0) {
        return {text: submission.submittedAt.isoWithoutOffset, safe: true};
      } else {
        const value = ((submission.values || []).find(s => s.fieldId === col.fieldId) || {}).value;
        return value || {text: '', safe: true};
      }
    })),
  };
};

const SubmissionTable = ({tableModel}) => {

  if (!tableModel || tableModel.length === 0) {
    return <div className="row justify-content-center">
      <div className="col-auto">No submission found</div>
    </div>
  }
  return <div>
    <div className="table-responsive">
      <table className="table table-striped">
        <thead>
        <tr>
          {tableModel.header.map((h, i) => <th key={i}>{h}</th>)}
        </tr>
        </thead>
        <tbody>
        {tableModel.body.map((row, i) =>
          <tr key={i}>
            {row.map((v, j) => <td key={j}>{v.safe ? v.text : escapeHtml(v.text)}</td>)}
          </tr>
        )}
        </tbody>
      </table>
    </div>
  </div>;

};

const PageActions = ({
  onDownloadPageClick, onPreviousPageClick, onNextPageClick,
  downloadPageButtonDisabled, previousPageButtonDisabled, nextPageButtonDisabled,
  navigationButtonsHidden
}) => {
  return <div className="row justify-content-end">
    <div className="col-auto">
      <button className="btn btn-primary btn-sm px-3"
              onClick={() => onDownloadPageClick()}
              disabled={downloadPageButtonDisabled}>
        Download page as csv
      </button>
    </div>
    {!navigationButtonsHidden &&
    <>
      <div className="col-auto">
        <button className="btn btn-dark btn-sm px-3"
                onClick={() => onPreviousPageClick()}
                disabled={previousPageButtonDisabled}>
          Previous page
        </button>
      </div>
      <div className="col-auto">
        <button className="btn btn-dark btn-sm px-4"
                onClick={() => onNextPageClick()}
                disabled={nextPageButtonDisabled}>
          Next page
        </button>
      </div>
    </>
    }
  </div>;
};

const FormSubmissions = () => {
  const {formId} = useParams();

  const [navFromKeys, setNavFromKeys] = useState([null]);
  const [currentNavFromKeyIndex, setCurrentNavFromKeyIndex] = useState(0);
  const [submissionsTableModel, setSubmissionsTableModel] = useState();

  useEffect(() => {
    setSubmissionsTableModel(null);
    customFormsV2Client
      .findFormSubmissionsWithValues(
        formId, 500, navFromKeys[currentNavFromKeyIndex], timezoneService.tryResolveTimezone()
      )
      .then(response => {
        setSubmissionsTableModel(submissionsToTableModel(response.submittedAtTimezone, response.items));
        if (currentNavFromKeyIndex === navFromKeys.length - 1) {
          setNavFromKeys([...navFromKeys, response.lastEvaluatedKey]);
        }
      })
      .catch(err => defaultErrorHandler(err));
  }, [currentNavFromKeyIndex]);

  const toCsvLine = row => row ?
    row.map(value => '"' + (value || '').replace(/"/g, '""') + '"').join(',') :
    '';

  const downloadPage = () => {
    const csv = toCsvLine(submissionsTableModel.header) + '\n' +
      (submissionsTableModel.body || []).map(row => toCsvLine(row.map(v => v.text))).join('\n');
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.target = '_blank';
    link.download = 'acerill-form-submissions.csv';
    link.click();
  };

  return <Section title="Form submissions" noMargin={true}>
    <div className="row justify-content-center mb-5">
      <div className="col-12">
        {!submissionsTableModel ?
          <CenteredSpinner/> :
          <>
            <SubmissionTable tableModel={submissionsTableModel}/>
            <PageActions onDownloadPageClick={() => downloadPage()}
                         onPreviousPageClick={() => setCurrentNavFromKeyIndex(currentNavFromKeyIndex - 1)}
                         onNextPageClick={() => setCurrentNavFromKeyIndex(currentNavFromKeyIndex + 1)}
                         downloadPageButtonDisabled={submissionsTableModel.body.length === 0}
                         previousPageButtonDisabled={currentNavFromKeyIndex === 0}
                         nextPageButtonDisabled={!navFromKeys[currentNavFromKeyIndex + 1]}
                         navigationButtonsHidden={currentNavFromKeyIndex === 0 && !navFromKeys[1]}/>
          </>
        }
      </div>
    </div>
  </Section>;
};

export default FormSubmissions;